/**
 * Font Optimization for Public Wi-Fi Networks
 * 
 * This utility ensures Material Design Icons load reliably on restricted networks
 * by implementing font preloading, caching, and graceful fallbacks.
 */

interface FontLoadResult {
  success: boolean
  error?: string
  cached: boolean
}

class FontOptimizationService {
  private static instance: FontOptimizationService
  private fontsLoaded = false
  private fontCheckInterval: number | null = null

  static getInstance(): FontOptimizationService {
    if (!FontOptimizationService.instance) {
      FontOptimizationService.instance = new FontOptimizationService()
    }
    return FontOptimizationService.instance
  }

  /**
   * Initialize font optimization on app startup
   */
  async initialize(): Promise<void> {
    console.log('🔤 Initializing font optimization for public Wi-Fi networks...')
    
    // Start monitoring font loading
    this.startFontMonitoring()
    
    // Check if fonts are already loaded
    await this.checkFontAvailability()
  }

  /**
   * Check if Material Design Icons font is available.
   *
   * Intentionally does NOT add the `.mdi-fallback` class on failure — every
   * DIY detection we've tried has misfired on some browser (width-measuring
   * reports false on Android Chrome, document.fonts.check reports false on
   * iOS WKWebView before any element actually renders with the font). Both
   * misfires painted circle placeholders over every icon site-wide. The
   * browser's native @font-face fallback chain is already robust, so let it
   * do the work; we only flag success so `.mdi-loaded` hooks still fire.
   */
  private async checkFontAvailability(): Promise<FontLoadResult> {
    try {
      if (typeof document === 'undefined' || !document.fonts) {
        return { success: false, error: 'document.fonts unavailable', cached: false }
      }
      await document.fonts.ready
      if (document.fonts.check('16px "Material Design Icons"')) {
        this.fontsLoaded = true
        document.documentElement.classList.add('mdi-loaded')
        return { success: true, cached: false }
      }
      return { success: false, error: 'Font not loaded yet', cached: false }
    } catch (error) {
      return { success: false, error: String(error), cached: false }
    }
  }

  /**
   * Monitor font loading with periodic checks
   */
  private startFontMonitoring(): void {
    let attempts = 0
    const maxAttempts = 20
    this.fontCheckInterval = window.setInterval(async () => {
      attempts++
      if (this.fontsLoaded || attempts >= maxAttempts) {
        if (this.fontCheckInterval) {
          clearInterval(this.fontCheckInterval)
          this.fontCheckInterval = null
        }
        return
      }
      const result = await this.checkFontAvailability()
      if (result.success && this.fontCheckInterval) {
        clearInterval(this.fontCheckInterval)
        this.fontCheckInterval = null
      }
    }, 500)
  }

  /**
   * Preload critical MDI font files for better cache hit rate
   */
  preloadFonts(): void {
    // Get the font URLs from the built assets
    const link1 = document.createElement('link')
    link1.rel = 'preload'
    link1.as = 'font'
    link1.type = 'font/woff2'
    link1.crossOrigin = 'anonymous'
    // Note: The actual URL will be determined by Vite build
    
    // Add to head for early loading
    document.head.appendChild(link1)
    
    console.log('🚀 Preloading Material Design Icons fonts for better cache performance')
  }

  /**
   * Add CSS fallbacks for when fonts fail to load
   */
  addFallbackStyles(): void {
    const style = document.createElement('style')
    style.id = 'mdi-fallback-styles'
    style.textContent = `
      /* Fallback styles when MDI font fails to load on public Wi-Fi */
      .mdi-fallback .mdi-close::before { content: '✕'; }
      .mdi-fallback .mdi-menu::before { content: '☰'; }
      .mdi-fallback .mdi-share-variant::before { content: '↗'; }
      .mdi-fallback .mdi-content-copy::before { content: '📋'; }
      .mdi-fallback .mdi-volume-high::before { content: '🔊'; }
      .mdi-fallback .mdi-stop::before { content: '⏹'; }
      .mdi-fallback .mdi-chevron-left::before { content: '‹'; }
      .mdi-fallback .mdi-chevron-right::before { content: '›'; }
      .mdi-fallback .mdi-refresh::before { content: '↻'; }
      .mdi-fallback .mdi-crosshairs-gps::before { content: '⌖'; }
      .mdi-fallback .mdi-microphone::before { content: '🎤'; }
      .mdi-fallback .mdi-microphone-outline::before { content: '🎙'; }
      .mdi-fallback .mdi-table::before { content: '⚏'; }
      .mdi-fallback .mdi-weather-partly-cloudy::before { content: '⛅'; }
      .mdi-fallback .mdi-text::before { content: 'T'; }
      
      /* Ensure fallback icons are visible */
      .mdi-fallback .mdi::before {
        font-family: system-ui, -apple-system, sans-serif !important;
        font-size: 1.2em;
        line-height: 1;
      }
      
      /* Hide empty icons when font fails */
      .mdi-fallback .mdi:not([class*="::before"])::before {
        content: '◯';
        opacity: 0.5;
      }
    `
    
    document.head.appendChild(style)
    console.log('🎨 Added MDI fallback styles for public Wi-Fi compatibility')
  }

  /**
   * Get loading status
   */
  isLoaded(): boolean {
    return this.fontsLoaded
  }

  /**
   * Cleanup monitoring
   */
  destroy(): void {
    if (this.fontCheckInterval) {
      clearInterval(this.fontCheckInterval)
      this.fontCheckInterval = null
    }
  }
}

// Export singleton instance
export const fontOptimization = FontOptimizationService.getInstance()

// Auto-initialize fallback styles immediately
if (typeof document !== 'undefined') {
  fontOptimization.addFallbackStyles()
}

export default fontOptimization